import { emitMaintenanceMode } from '@/shared/lib/maintenanceMode'
import { isInPaymentReturnGrace } from '@/shared/lib/paymentReturnBootstrap'
import { apiV1 } from '@/shared/api/apiPrefix'
import { emitSessionActivity, isSessionActivityExemptPath } from '@/shared/lib/sessionActivity'
import { emitSessionExpired } from '@/shared/lib/sessionExpiry'
import { isUnauthenticatedProblem, redirectToLoginAfterUnauthenticated } from '@/shared/lib/authSessionCleanup'
import { emitRateLimited } from '@/shared/lib/rateLimit'

export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'

/** How a 401 mid-session is surfaced to the user. */
export type AuthFailureMode = 'modal' | 'probe' | 'silent'

export type HttpRequestOptions = {
  method?: HttpMethod
  headers?: Record<string, string>
  body?: unknown
  /** When true, a 2xx response does not reset the client idle timer (background poll). */
  background?: boolean
  /** Override default 401 handling for this request. */
  authFailure?: AuthFailureMode
}

export type HttpFormUploadOptions = {
  onUploadProgress?: (percent: number) => void
}

export type HttpResponse<T> = {
  data: T
}

export type ProblemDetails = {
  type: string
  title: string
  status: number
  detail?: string | null
  instance?: string
  [key: string]: unknown
}

export class HttpProblemError extends Error {
  readonly problem: ProblemDetails
  readonly status: number
  readonly retryAfterSeconds: number | null

  constructor(problem: ProblemDetails, retryAfterSeconds: number | null = null) {
    super(problem.title)
    this.problem = problem
    this.status = problem.status
    this.retryAfterSeconds = retryAfterSeconds
  }
}

export class HttpValidationError extends Error {
  readonly status = 422
  readonly errors: Record<string, string[]>

  constructor(errors: Record<string, string[]>) {
    super('HTTP 422')
    this.errors = errors
  }
}

export class HttpRateLimitedError extends Error {
  readonly status = 429
  readonly retryAfterSeconds: number | null

  constructor(retryAfterSeconds: number | null) {
    super('HTTP 429')
    this.retryAfterSeconds = retryAfterSeconds
  }
}

function parseRetryAfter(header: string | null): number | null {
  if (header === null || header.trim() === '') {
    return null
  }
  const seconds = Number(header)
  if (Number.isFinite(seconds) && seconds >= 0) {
    return Math.ceil(seconds)
  }
  const at = Date.parse(header)

  return Number.isNaN(at) ? null : Math.max(0, Math.ceil((at - Date.now()) / 1000))
}

function retryAfterFromBody(body: unknown): number | null {
  if (body === null || typeof body !== 'object' || !('retry_after' in body)) {
    return null
  }

  const value = (body as { retry_after: unknown }).retry_after
  return typeof value === 'number' || typeof value === 'string' ? parseRetryAfter(String(value)) : null
}

function readValidationErrors(body: unknown): Record<string, string[]> | null {
  if (body === null || typeof body !== 'object' || !('errors' in body)) {
    return null
  }
  const raw = (body as { errors: unknown }).errors
  if (raw === null || typeof raw !== 'object' || Array.isArray(raw)) {
    return null
  }
  const out: Record<string, string[]> = {}
  for (const [field, messages] of Object.entries(raw as Record<string, unknown>)) {
    if (Array.isArray(messages)) {
      out[field] = messages.filter((m): m is string => typeof m === 'string')
    }
  }

  return Object.keys(out).length > 0 ? out : null
}

export type HttpClientOptions = {
  headers?: Record<string, string>
  /** When true, a 2xx response does not reset the client idle timer (background poll). */
  background?: boolean
  authFailure?: AuthFailureMode
}

/** Refresh CSRF before a mutation when the last authenticated POST is older than this. */
export const CSRF_STALE_THRESHOLD_MS = 5 * 60 * 1000

export const httpClient = {
  get: async <TResponse>(path: string, options: HttpClientOptions = {}): Promise<HttpResponse<TResponse>> => ({
    data: await httpRequest<TResponse>(path, {
      method: 'GET',
      headers: options.headers,
      background: options.background,
      authFailure: options.authFailure,
    }),
  }),
  post: async <TResponse>(
    path: string,
    body?: unknown,
    options: HttpClientOptions = {},
  ): Promise<HttpResponse<TResponse>> => ({
    data: await httpRequest<TResponse>(path, {
      method: 'POST',
      body,
      headers: options.headers,
      authFailure: options.authFailure,
    }),
  }),
  patch: async <TResponse>(
    path: string,
    body?: unknown,
    options: HttpClientOptions = {},
  ): Promise<HttpResponse<TResponse>> => ({
    data: await httpRequest<TResponse>(path, {
      method: 'PATCH',
      body,
      headers: options.headers,
      authFailure: options.authFailure,
    }),
  }),
  put: async <TResponse>(
    path: string,
    body?: unknown,
    options: HttpClientOptions = {},
  ): Promise<HttpResponse<TResponse>> => ({
    data: await httpRequest<TResponse>(path, {
      method: 'PUT',
      body,
      headers: options.headers,
      authFailure: options.authFailure,
    }),
  }),
  delete: async <TResponse>(
    path: string,
    options: HttpClientOptions & { body?: unknown } = {},
  ): Promise<HttpResponse<TResponse>> => ({
    data: await httpRequest<TResponse>(path, {
      method: 'DELETE',
      headers: options.headers,
      body: options.body,
      authFailure: options.authFailure,
    }),
  }),
  postForm: async <TResponse>(
    path: string,
    formData: FormData,
    options: HttpClientOptions & HttpFormUploadOptions = {},
  ): Promise<HttpResponse<TResponse>> => ({
    data: await httpFormRequest<TResponse>(path, formData, options),
  }),
}

export function resetHttpClientAuthStateForTests(): void {
  isRedirectingOnUnauthorized = false
  csrfRefreshPromise = null
  sessionSuspect = false
  lastAuthenticatedMutationAt = 0
  if (deferredAuthProbeTimer !== null) {
    clearTimeout(deferredAuthProbeTimer)
    deferredAuthProbeTimer = null
  }
}

export function isSessionSuspectForTests(): boolean {
  return sessionSuspect
}

export async function ensureCsrfCookie(): Promise<void> {
  const baseUrl = import.meta.env.VITE_API_BASE_URL as string | undefined
  const url = `${baseUrl ?? ''}/sanctum/csrf-cookie`

  const res = await fetch(url, {
    method: 'GET',
    credentials: 'include',
    headers: { Accept: 'application/json' },
  })

  if (!res.ok) {
    throw new Error(`HTTP ${res.status}`)
  }
}

/** Pre-fetch CSRF when no authenticated mutation ran recently (wizard lease refresh). */
export async function ensureFreshCsrfIfStale(thresholdMs = CSRF_STALE_THRESHOLD_MS): Promise<void> {
  if (Date.now() - lastAuthenticatedMutationAt < thresholdMs) {
    return
  }
  await ensureCsrfCookie()
}

/**
 * Redirection de ré-authentification sur 401 **mid-session** (la session a expiré
 * pendant l'usage). Idempotent (une seule navigation) et neutralisé sur les pages
 * `/auth/*` pour ne pas hijacker un 401 de login (mauvais identifiants). Le gating
 * de navigation initial reste assuré par `requireAuth` (beforeLoad).
 */
let isRedirectingOnUnauthorized = false
let csrfRefreshPromise: Promise<void> | null = null
let deferredAuthProbeTimer: ReturnType<typeof setTimeout> | null = null
let sessionSuspect = false
let lastAuthenticatedMutationAt = 0

function markAuthenticatedMutation(): void {
  lastAuthenticatedMutationAt = Date.now()
  sessionSuspect = false
}

function resolveAuthFailureMode(path: string, options: HttpRequestOptions): AuthFailureMode {
  if (options.authFailure !== undefined) {
    return options.authFailure
  }
  if (options.background) {
    return 'probe'
  }
  const normalized = path.split('?')[0] ?? path
  if (normalized.includes('/auth/refresh')) {
    return 'silent'
  }
  if (normalized.includes('/reservations/block-slot')) {
    return 'probe'
  }
  return 'modal'
}

function scheduleDeferredAuthProbe(): void {
  if (deferredAuthProbeTimer !== null || typeof window === 'undefined') {
    return
  }
  const delay = Math.max(250, isInPaymentReturnGrace() ? 500 : 0)
  deferredAuthProbeTimer = setTimeout(() => {
    deferredAuthProbeTimer = null
    void probeAuthAfterPaymentReturn()
  }, delay)
}

/**
 * Exported for callers outside the normal `httpRequest` pipeline (e.g. `openReservationPdf.ts`,
 * which uses a raw `fetch` for streamed PDF bytes) that still need the same probe-before-modal
 * false-positive protection on a 401.
 */
export async function probeAuthSession(): Promise<boolean> {
  try {
    await refreshCsrfCookieSerialized()
    const baseUrl = import.meta.env.VITE_API_BASE_URL as string | undefined
    const url = `${baseUrl ?? ''}${apiV1('/me')}`
    const res = await fetch(url, {
      method: 'GET',
      credentials: 'include',
      headers: { Accept: 'application/json' },
    })
    if (res.status === 401) {
      sessionSuspect = true
      return false
    }
    if (res.ok) {
      sessionSuspect = false
    }
    return true
  } catch {
    // Network blip - do not treat as confirmed logout.
    return true
  }
}

async function probeAuthAfterPaymentReturn(): Promise<void> {
  if (isInPaymentReturnGrace()) {
    scheduleDeferredAuthProbe()
    return
  }

  const alive = await probeAuthSession()
  if (!alive) {
    handleUnauthorized(true)
  }
}

async function refreshCsrfCookieSerialized(): Promise<void> {
  if (csrfRefreshPromise) {
    await csrfRefreshPromise
    return
  }

  csrfRefreshPromise = ensureCsrfCookie().finally(() => {
    csrfRefreshPromise = null
  })
  await csrfRefreshPromise
}

async function handleCsrfMismatch(
  retry: () => Promise<Response>,
  authFailure: AuthFailureMode,
): Promise<Response> {
  await refreshCsrfCookieSerialized()
  const retried = await retry()
  // Still 419 after a single CSRF refresh → treat as mid-session expiry.
  if (retried.status === 419) {
    await handleAuthFailure401(authFailure, true, 'csrf')
  }
  return retried
}

async function handleAuthFailure401(
  mode: AuthFailureMode,
  force = false,
  reason: 'session' | 'csrf' = 'session',
): Promise<void> {
  if (mode === 'silent') {
    sessionSuspect = true
    return
  }
  // Both 'probe' and 'modal' re-verify via /me before acting - a single 401 can be a
  // transient blip, and 'modal' is the default for normal requests so a false positive
  // here would surface the session-expired modal on a session that is actually still alive.
  const alive = await probeAuthSession()
  if (!alive) {
    handleUnauthorized(force, reason)
  }
}

/** Exported for the same out-of-pipeline callers as {@link probeAuthSession} (see its doc comment). */
export function handleUnauthorized(force = false, reason: 'session' | 'csrf' = 'session'): void {
  if (typeof window === 'undefined' || isRedirectingOnUnauthorized) {
    return
  }
  if (!force && isInPaymentReturnGrace()) {
    scheduleDeferredAuthProbe()
    return
  }
  // Public pages (auth + legal/cookies + QR hub): 401 is expected for anonymous headers - skip re-auth.
  if (
    window.location.pathname.startsWith('/auth') ||
    window.location.pathname.startsWith('/legal/') ||
    window.location.pathname.startsWith('/r/')
  ) {
    return
  }
  isRedirectingOnUnauthorized = true
  try {
    sessionStorage.setItem('auth_redirect_reason', 'auth_required')
    sessionStorage.setItem('auth_redirect_from', `${window.location.pathname}${window.location.search}`)
  } catch {
    // ignore storage failures
  }
  // Session-expired modal takes over when mounted; otherwise fall back to hard redirect.
  if (!emitSessionExpired(reason)) {
    window.location.assign('/auth/login?reason=auth_required')
  }
}

/**
 * Call when the session-expired modal is dismissed without reconnecting (user chose to
 * keep browsing). Without this, `isRedirectingOnUnauthorized` stays true for the rest of
 * the page lifetime and a later, genuinely-confirmed 401 would be silently swallowed.
 */
export function acknowledgeSessionExpiredDismissed(): void {
  isRedirectingOnUnauthorized = false
}

/**
 * App-level maintenance (503, RFC 7807). The maintenance middleware returns 503 for
 * non-admin callers; surface the takeover so the app is not left showing broken data.
 * `retry_after`/`detail` extension members feed the maintenance page copy when present.
 */
function handleMaintenance(body: unknown): void {
  const problem = body && typeof body === 'object' ? (body as Record<string, unknown>) : {}
  emitMaintenanceMode({
    message: typeof problem.detail === 'string' ? problem.detail : null,
    retryAfter: typeof problem.retry_after === 'string' ? problem.retry_after : null,
  })
}

function readCookie(name: string): string | null {
  if (typeof document === 'undefined') return null
  const match = document.cookie.match(new RegExp(`(?:^|; )${name.replaceAll('.', '\\.')}=([^;]*)`))
  return match ? match[1] : null
}

function isFormDataBody(body: unknown): body is FormData {
  return typeof FormData !== 'undefined' && body instanceof FormData
}

function buildJsonRequestBody(body: unknown): BodyInit | undefined {
  if (body === undefined) {
    return undefined
  }
  if (isFormDataBody(body)) {
    return body
  }
  return JSON.stringify(body)
}

function buildRequestHeaders(body: unknown, extra?: Record<string, string>): Record<string, string> {
  const xsrf = readCookie('XSRF-TOKEN')
  const xsrfHeader = xsrf ? decodeURIComponent(xsrf) : null

  return {
    Accept: 'application/json',
    ...(body === undefined || isFormDataBody(body) ? {} : { 'Content-Type': 'application/json' }),
    ...(xsrfHeader ? { 'X-XSRF-TOKEN': xsrfHeader } : {}),
    ...(extra ?? {}),
  }
}

async function parseResponseBody<TResponse>(res: Response): Promise<TResponse> {
  if (res.status === 204) {
    return null as TResponse
  }

  const text = await res.text()
  if (text === '') {
    return null as TResponse
  }

  return JSON.parse(text) as TResponse
}

async function handleFailedResponse(res: Response, path: string, options: HttpRequestOptions): Promise<never> {
  let body: unknown
  try {
    body = await res.json()
  } catch {
    body = null
  }

  if (res.status === 401) {
    if (isUnauthenticatedProblem(body)) {
      redirectToLoginAfterUnauthenticated()
    } else {
      const mode = resolveAuthFailureMode(path, options)
      await handleAuthFailure401(mode)
    }
  }

  if (res.status === 503) {
    handleMaintenance(body)
  }

  const retryAfterSeconds = res.status === 429
    ? parseRetryAfter(res.headers.get('Retry-After')) ?? retryAfterFromBody(body)
    : null

  if (res.status === 429) {
    emitRateLimited({ path, retryAfterSeconds })
  }

  if (body && typeof body === 'object' && 'type' in body && 'title' in body && 'status' in body) {
    throw new HttpProblemError(body as ProblemDetails, retryAfterSeconds)
  }

  if (res.status === 429) {
    throw new HttpRateLimitedError(retryAfterSeconds)
  }

  if (res.status === 422) {
    const errors = readValidationErrors(body)
    if (errors !== null) {
      throw new HttpValidationError(errors)
    }
  }

  throw new Error(`HTTP ${res.status}`)
}

export async function httpRequest<TResponse>(
  path: string,
  options: HttpRequestOptions = {},
): Promise<TResponse> {
  const baseUrl = import.meta.env.VITE_API_BASE_URL as string | undefined
  const url = `${baseUrl ?? ''}${path}`
  const authFailure = resolveAuthFailureMode(path, options)

  const performRequest = (): Promise<Response> =>
    fetch(url, {
      method: options.method ?? 'GET',
      headers: buildRequestHeaders(options.body, options.headers),
      body: buildJsonRequestBody(options.body),
      credentials: 'include',
    })

  let res = await performRequest()

  if (res.status === 419 && (options.method ?? 'GET') !== 'GET') {
    res = await handleCsrfMismatch(performRequest, authFailure)
  }

  if (!res.ok) {
    return handleFailedResponse(res, path, options)
  }

  const method = options.method ?? 'GET'
  if (method !== 'GET') {
    markAuthenticatedMutation()
  }

  if (!options.background && !isSessionActivityExemptPath(path)) {
    emitSessionActivity()
  }

  return parseResponseBody<TResponse>(res)
}

export async function httpFormRequest<TResponse>(
  path: string,
  formData: FormData,
  options: HttpClientOptions & HttpFormUploadOptions = {},
): Promise<TResponse> {
  const baseUrl = import.meta.env.VITE_API_BASE_URL as string | undefined
  const url = `${baseUrl ?? ''}${path}`
  const authFailure = resolveAuthFailureMode(path, options)

  const performRequest = (): Promise<{ status: number; responseText: string; retryAfter: string | null }> =>
    new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest()
      xhr.open('POST', url)
      xhr.withCredentials = true
      xhr.responseType = 'text'

      const headers = buildRequestHeaders(formData, options.headers)
      for (const [key, value] of Object.entries(headers)) {
        xhr.setRequestHeader(key, value)
      }

      if (options.onUploadProgress) {
        xhr.upload.addEventListener('progress', (event) => {
          if (!event.lengthComputable) {
            return
          }
          const percent = Math.round((event.loaded / event.total) * 100)
          options.onUploadProgress?.(percent)
        })
      }

      xhr.addEventListener('load', () => {
        resolve({
          status: xhr.status,
          responseText: xhr.responseText,
          retryAfter: xhr.getResponseHeader('Retry-After'),
        })
      })
      xhr.addEventListener('error', () => reject(new Error('Network error')))
      xhr.addEventListener('abort', () => reject(new Error('Upload aborted')))

      xhr.send(formData)
    })

  let result = await performRequest()

  if (result.status === 419) {
    await refreshCsrfCookieSerialized()
    result = await performRequest()
    if (result.status === 419) {
      await handleAuthFailure401(authFailure, true, 'csrf')
    }
  }

  if (result.status < 200 || result.status >= 300) {
    const fakeResponse = new Response(result.responseText, {
      status: result.status,
      headers: result.retryAfter === null ? undefined : { 'Retry-After': result.retryAfter },
    })
    return handleFailedResponse(fakeResponse, path, { authFailure })
  }

  markAuthenticatedMutation()

  if (!options.background && !isSessionActivityExemptPath(path)) {
    emitSessionActivity()
  }

  if (result.status === 204 || result.responseText === '') {
    return null as TResponse
  }

  return JSON.parse(result.responseText) as TResponse
}
